import { motion } from 'framer-motion';

interface LevelBadgeProps {
  level: number;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showLabel?: boolean;
  animated?: boolean;
}

export function LevelBadge({
  level,
  size = 'md',
  showLabel = true,
  animated = true,
}: LevelBadgeProps) {
  const sizeClasses = {
    sm: 'px-2 py-1 text-sm',
    md: 'px-3 py-1.5 text-base',
    lg: 'px-4 py-2 text-lg',
    xl: 'px-5 py-3 text-2xl',
  };

  const content = (
    <div
      className={`flex items-center gap-1 ${sizeClasses[size]} rounded-xl bg-royal-gradient text-white font-bold shadow-glow`}
    >
      {showLabel && <span className="opacity-80 font-normal">Lv.</span>}
      <span>{level}</span>
    </div>
  );

  if (!animated) {
    return content;
  }

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      whileHover={{ scale: 1.05 }}
      transition={{ type: 'spring', stiffness: 300 }}
    >
      {content}
    </motion.div>
  );
}

interface LevelProgressProps {
  level: number;
  totalXp: number;
  progress: number;
}

export function LevelProgress({ level, totalXp, progress }: LevelProgressProps) {
  return (
    <div className="flex items-center gap-3">
      <LevelBadge level={level} size="md" />
      <div className="flex-1">
        <div className="flex justify-between items-center mb-1">
          <span className="text-xs text-slate-400">Total XP</span>
          <span className="text-xs font-medium text-amber-400">
            {totalXp.toLocaleString()} XP
          </span>
        </div>
        <div className="bg-charcoal-800 rounded-full h-2 overflow-hidden border border-charcoal-700/50">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="h-full bg-royal-gradient"
          />
        </div>
      </div>
    </div>
  );
}
